import { InvoiceProvider, PaymentProvider } from '../client-site';

export enum PaymentRequestStatus {
  Sent = 'sent',
  InProgress = 'in-progress',
  Failed = 'failed',
  Success = 'success',
  // Card saved at booking; charge happens later (cron at scheduledChargeDate, or
  // manually by the business when manualChargeOnly is set)
  Scheduled = 'scheduled',
  Cancelled = 'cancelled',
  // A successful payment that was fully or partially refunded
  Refunded = 'refunded',
  PartiallyRefunded = 'partially-refunded'
}

export interface IRefundEntry {
  amount: number;
  reason?: string;
  refundedAt: Date | string;
  // Admin userId that issued the refund
  refundedBy?: string;
  // Provider refund transaction id (when refunded through the gateway)
  transactionId?: string;
  // true when the gateway couldn't perform the refund and it must be settled manually
  manual?: boolean;
}

export enum PaymentRequestReason {
  Visit = 'visit',
  Voucher = 'voucher',
  Other = 'other',
  Advanced = 'advanced',
  Schedule = 'schedule',
  BundlePack = 'bundle-pack',
  ProductOrder = 'product-order'
}

export enum PaymentMethod {
  Cash = 'cash',
  Credit = 'credit',
  BankTransfer = 'bank-transfer',
  Bit = 'bit',
  PayBox = 'pay-box',
  ApplePay = 'apple-pay',
  GooglePay = 'google-pay',
  LoyaltyProgramDiscount = 'loyalty-program-discount',
  LoyaltyProgramPoints = 'loyalty-program-points',
}

export interface IPaymentSplit {
  subtotal: number;                   // Original amount before discounts (services + products)
  servicesSubtotal: number;           // Services subtotal (discounts apply here)
  productsSubtotal: number;           // Products subtotal (always paid in full)

  voucherDiscount?: {
    voucherId: string;
    amount: number;                   // Discount amount (applied to services only)
  };

  bundlePackDiscount?: {
    bundlePackId: string;
    amount: number;                   // Discount amount (covers full service cost)
    serviceIds?: string[];            // Covered services paid by the pack — one usage consumed per entry
  };

  couponDiscount?: {
    couponId: string;
    code: string;
    amount: number;                   // Discount amount (applied to services only)
  };

  loyaltyDiscount?: {
    pointsUsed?: number;              // If points-based
    discountAmount?: number;          // If discount-based
    amount: number;                   // Total discount amount (applied to services only)
  };

  /** Customer credit (יתרה) used as a discount; deducted from the balance on success */
  balanceDiscount?: {
    amount: number;
  };

  paymentType: 'full' | 'advanced';   // User's choice
  advancedPercentage?: number;        // If advanced payment (applies to services only)

  finalAmount: number;                // Amount charged to credit card

  // Legacy fields (kept for backward compatibility)
  /** @deprecated Use loyaltyDiscount instead */
  loyaltyProgram?: {
    amount: number;
    usedPointsOrDiscounts: number;
  };
  /** @deprecated Use finalAmount instead */
  advanced?: number;
  /** @deprecated */
  paymentMethod?: number;
}

export interface IPaymentPaymeExtras {
  installments?: {
    amount: number;
    firstPaymentValue: number;
    otherPaymentValue: number;
  };
  cardMask?: string;
}


export interface IPaymentRequest {
  _id?: string;
  uuid: string;
  bid: string;
  paymentReason: PaymentRequestReason;
  paymentMethod: PaymentMethod;
  appointmentId?: string | string[];
  voucherId?: string;
  bundlePackId?: string;
  couponId?: string;
  productOrderId?: string;
  status: PaymentRequestStatus;
  price: number;
  chargedDate?: Date | string;
  overridePrices?: Record<string, number>;
  signature?: string;
  transactionNumber?: string;
  paidPrice?: number;
  paidByPhone: string;
  ezCountInvoiceLink?: string;
  /** The issued receipt's real document number (PayMe invoice_doc_number) */
  invoiceDocNumber?: string;
  isPayForProducts?: boolean;
  useLoyaltyRewards?: boolean;
  paymentSplit?: IPaymentSplit;
  provider: PaymentProvider | InvoiceProvider;
  note?: string;
  paymeExtras?: IPaymentPaymeExtras;
  createdAt?: string;
  /** Pays off the customer's debt (חוב); the paid amount is credited to their balance on success */
  settlesCustomerDebt?: boolean;
  /** PayMe POS terminal the charge was sent to (card-present charge on a physical terminal) */
  posTerminalId?: string;
  /** When the current terminal attempt was sent */
  posSentAt?: Date | string;
  /** The provider's reason for the last failed attempt (e.g. a declined card on the terminal) */
  failureReason?: string;

  // ===== Saved-card (charge-later) flow =====
  // AES-encrypted permanent PayMe token (buyer_key). Never exposed to the frontend.
  savedCardToken?: string;
  // When the cron should auto-charge (before-appointment mode only)
  scheduledChargeDate?: Date | string;
  // When the customer accepted the cancellation policy / delayed-charge consent
  saveCardConsentAt?: Date | string;
  // save-card mode: only the business may charge, manually, up to the consented amount
  manualChargeOnly?: boolean;
  // Card-present charge typed in by the business (created without sending a link): the
  // payment form allows up to 12 installments and hides Bit/wallets for it.
  manualCharge?: boolean;
  // Admin asked for this specific request to collect + store the card instead of charging
  // it, regardless of the business-wide scheduleChargeMode.
  collectCardOnly?: boolean;
  // When an uncharged token must be discarded (appointment + 7 days)
  tokenExpiresAt?: Date | string;

  // ===== Refunds =====
  // Total amount refunded so far across all refund entries
  refundedAmount?: number;
  refunds?: IRefundEntry[];

  // ===== External (API-created) payment forms =====
  // Caller-supplied id, echoed back on the callback so the caller can reconcile
  externalId?: string;
  // Where to POST the completion notification
  externalCallbackUrl?: string;
  // Payer name supplied by the caller (there is no customer record for these)
  externalPayerName?: string;
  // Payer phone supplied by the caller. Deliberately NOT stored as paidByPhone: that field
  // links a payment to a TimeIt customer, and an API caller's payer may be a stranger.
  externalPayerPhone?: string;
  // When the completion notification was delivered (guards against double-firing)
  externalCallbackSentAt?: Date | string;
}

/**
 * Public API: create a payment form for a business that has `externalPaymentsEnabled`
 * switched on and an `externalPaymentsApiKey` set in the DB. PayMe only.
 * Authenticated with that key in the `x-api-key` request header.
 */
export interface ICreateExternalPaymentDto {
  bid: string;
  amount: number;
  /** Caller's own identifier, echoed back on the callback */
  externalId: string;
  /** Absolute https URL we POST the completion notification to */
  callbackUrl: string;
  customerName?: string;
  /**
   * Payer phone. PayMe's hosted-fields validator requires one, so when it is omitted here
   * the payer is asked for it on the form itself.
   */
  customerPhone?: string;
  /** Shown as the payment's purpose on the form and the invoice */
  description?: string;
}

export interface ICreateExternalPaymentResponse {
  /** TimeIt payment request uuid */
  uuid: string;
  /** Hosted payment form to send the payer to */
  paymentUrl: string;
}

/** Body POSTed to the caller's callbackUrl once the payment succeeds */
export interface IExternalPaymentCallbackPayload {
  externalId: string;
  paymentRequestUuid: string;
  bid: string;
  status: PaymentRequestStatus.Success;
  amount: number;
  transactionNumber?: string;
  chargedDate?: string;
}
